import { computed } from 'mobx'
import { task } from 'mobx-task'
import type { GetCurrentMemberFn } from '@/features/iam'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { Revalidation } from '@/infra/revalidation'
import { type Job } from '@/features/jobs/Job'
import { type InfoGatheringStore } from '@/features/jobs/info-gathering/InfoGatheringStore'
import { ViewModelList } from '@/infra/view-model-list'
import { PersonalProfileViewModel } from '@/features/jobs/info-gathering/individual-info/PersonalProfileViewModel'
import { SpousalProfileViewModel } from '@/features/jobs/info-gathering/individual-info/SpousalProfileViewModel'
import { DependentProfileViewModel } from '@/features/jobs/info-gathering/individual-info/DependentProfileViewModel'
import { BusinessInfoViewModel } from '@/features/jobs/info-gathering/business-info/BusinessInfoViewModel'
import { TaxRefundMethodViewModel } from '@/features/jobs/info-gathering/tax-refund-method/TaxRefundMethodViewModel'
import { type IndividualProfileStore } from '@/features/jobs/info-gathering/individual-info/IndividualProfileStore'
import { type BusinessProfileStore } from '@/features/jobs/info-gathering/business-info/BusinessProfileStore'
import { type TaxRefundMethodStore } from '@/features/jobs/info-gathering/tax-refund-method/TaxRefundMethodStore'
import type {
  CapturedInformationTab,
  DetailsViewModel,
  ResponsesInformationTab,
} from '@/features/jobs/details/DetailsViewModel'
import { type InfoGatheringCardViewModel } from '@/features/jobs/details/components/InfoGatheringCard/InfoGatheringCardViewModel'
import { type NotesTabViewModel } from '@/features/jobs/details/components/CapturedInformationCard/NotesTabViewModel'
import { type EditPersonalProfileDialogViewModel } from '@/features/jobs/info-gathering/individual-info/components/EditPersonalProfileDialog/EditPersonalProfileDialogViewModel'
import { type EditBusinessProfileDialogViewModel } from '@/features/jobs/info-gathering/business-info/components/EditBusinessProfileDialog/EditBusinessProfileDialogViewModel'
import { type EditRefundDialogViewModel } from '@/features/jobs/info-gathering/tax-refund-method/components/EditRefundDialog/EditRefundDialogViewModel'
import { type DocumentRequestDialogViewModel } from '@/features/jobs/details/components/DocumentRequestDialog/DocumentRequestDialogViewModel'

export interface IntakePaneTab<T extends string> {
  value: T
  label: string
  testId: string
}

export interface CapturedInformationTabItem {
  id: CapturedInformationTab
  label: string
  testId: string
  highlighted?: boolean
}

/**
 * What the client told us about themselves for a job: the intake answers,
 * their profiles, refund method and notes.
 */
export class JobCapturedInformationViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()
  private readonly _revalidation = new Revalidation(() => this.fetch.pending)

  private readonly _personalProfileVms = new ViewModelList({
    models: () =>
      toList(this.checklist?.personalInfoSection?.personalInfoSupportingItem),
    create: (item) =>
      new PersonalProfileViewModel(
        this._job,
        item,
        this._individualProfileStore,
      ),
    activate: (vm) => vm.activate(),
    deactivate: (vm) => vm.deactivate(),
  })

  private readonly _spousalProfileVms = new ViewModelList({
    models: () =>
      toList(this.checklist?.personalInfoSection?.spousalInfoSupportingItem),
    create: (item) =>
      new SpousalProfileViewModel(
        this._job,
        item,
        this._individualProfileStore,
      ),
    activate: (vm) => vm.activate(),
    deactivate: (vm) => vm.deactivate(),
  })

  private readonly _dependentProfileVms = new ViewModelList({
    models: () =>
      toList(this.checklist?.personalInfoSection?.dependentInfoSupportingItem),
    create: (item) =>
      new DependentProfileViewModel(
        this._job,
        item,
        this._individualProfileStore,
      ),
    activate: (vm) => vm.activate(),
    deactivate: (vm) => vm.deactivate(),
  })

  private readonly _businessInfoVms = new ViewModelList({
    models: () =>
      toList(this.checklist?.businessInfoSection?.businessInfoSupportingItem),
    create: (item) =>
      new BusinessInfoViewModel(this._job, item, this._businessProfileStore),
    activate: (vm) => vm.activate(),
    deactivate: (vm) => vm.deactivate(),
  })

  // The refund methods span the personal and business refund items, so their
  // view model is keyed on the checklist instead of a single item.
  private readonly _taxRefundMethodVms = new ViewModelList({
    models: () => toList(this.checklist),
    create: (checklist) =>
      new TaxRefundMethodViewModel(
        this._job,
        checklist,
        this._taxRefundMethodStore,
      ),
    activate: (vm) => vm.activate(),
    deactivate: (vm) => vm.deactivate(),
  })

  constructor(
    private readonly _job: Job,
    private readonly _detailsVm: DetailsViewModel,
    private readonly _infoGatheringStore: InfoGatheringStore,
    private readonly _getCurrentMember: GetCurrentMemberFn,
    public readonly infoGatheringCardVm: InfoGatheringCardViewModel,
    public readonly notesVm: NotesTabViewModel,
    private readonly _individualProfileStore: IndividualProfileStore,
    private readonly _businessProfileStore: BusinessProfileStore,
    private readonly _taxRefundMethodStore: TaxRefundMethodStore,
    private readonly _editPersonalProfileDialogVm: EditPersonalProfileDialogViewModel,
    private readonly _editBusinessProfileDialogVm: EditBusinessProfileDialogViewModel,
    private readonly _editRefundDialogVm: EditRefundDialogViewModel,
    public readonly documentRequestDialogVm: DocumentRequestDialogViewModel,
  ) {
    this._lifecycle.manage(this._personalProfileVms)
    this._lifecycle.manage(this._spousalProfileVms)
    this._lifecycle.manage(this._dependentProfileVms)
    this._lifecycle.manage(this._businessInfoVms)
    this._lifecycle.manage(this._taxRefundMethodVms)
  }

  /**
   * Whether the checklist is refetching while the previous one stays on
   * screen.
   */
  @computed
  get isRevalidating() {
    return this._revalidation.isRevalidating
  }

  /**
   * Whether the checklist is loading with nothing to show yet.
   */
  @computed
  get isFetchingInitial() {
    return this.fetch.pending && !this.isRevalidating
  }

  /**
   * The checklist of supporting items for the job.
   */
  @computed
  get checklist() {
    return this._infoGatheringStore.checklists.get(this._job.id)
  }

  /**
   * The personal profile, once the checklist requesting it has loaded.
   */
  @computed
  get personalProfileVm() {
    return this._personalProfileVms.items[0] ?? null
  }

  /**
   * The spousal profile, once the checklist requesting it has loaded.
   */
  @computed
  get spousalProfileVm() {
    return this._spousalProfileVms.items[0] ?? null
  }

  /**
   * The dependent profiles, once the checklist requesting them has loaded.
   */
  @computed
  get dependentProfileVm() {
    return this._dependentProfileVms.items[0] ?? null
  }

  /**
   * The business profile, once the checklist requesting it has loaded.
   */
  @computed
  get businessInfoVm() {
    return this._businessInfoVms.items[0] ?? null
  }

  /**
   * The refund methods, once the checklist has loaded.
   */
  @computed
  get taxRefundMethodVm() {
    return this._taxRefundMethodVms.items[0] ?? null
  }

  /**
   * Whether the current user can complete info gathering supporting items on
   * a client's behalf.
   */
  @computed
  get canEditCapturedInfo() {
    const { permissions } = this._getCurrentMember(this._job.workspace.id)
    return (
      permissions.workspaceJobInfoGatheringUpdate || permissions.adminJobUpdate
    )
  }

  /**
   * Edits the personal, spousal and dependent profiles, when the current user
   * can edit the captured information.
   */
  @computed
  get editPersonalProfileDialogVm() {
    return this.canEditCapturedInfo ? this._editPersonalProfileDialogVm : null
  }

  /**
   * Edits the business profile, when the current user can edit the captured
   * information.
   */
  @computed
  get editBusinessProfileDialogVm() {
    return this.canEditCapturedInfo ? this._editBusinessProfileDialogVm : null
  }

  /**
   * Edits the refund methods, when the current user can edit the captured
   * information.
   */
  @computed
  get editRefundDialogVm() {
    return this.canEditCapturedInfo ? this._editRefundDialogVm : null
  }

  @computed
  get intakeTabs(): IntakePaneTab<ResponsesInformationTab>[] {
    const tabs: IntakePaneTab<ResponsesInformationTab>[] = [
      { value: 'Questions', label: 'Answers', testId: 'questions-tab-item' },
    ]
    if (this._showsPersonalInfo) {
      tabs.push({
        value: 'PersonalInfo',
        label: 'Personal',
        testId: 'personal-info-tab-item',
      })
    }
    if (this._showsBusinessInfo) {
      tabs.push({
        value: 'BusinessInfo',
        label: 'Business',
        testId: 'business-tab-item',
      })
    }
    if (this._showsRefundInfo) {
      tabs.push({
        value: 'RefundInfo',
        label: 'Refund',
        testId: 'refund-tab-item',
      })
    }
    if (this.notesVm.areNotesEnabled) {
      tabs.push({
        value: 'Notes',
        label: this.notesVm.isInfoGatheringStep ? 'Notes' : 'Instructions',
        testId: 'notes-tab-item',
      })
    }
    return tabs
  }

  /**
   * The selected intake tab, or the answers when the selected one isn't
   * available for this job.
   */
  @computed
  get currentIntakeTab(): ResponsesInformationTab {
    const current = this._detailsVm.informationTabController.current.tab
    return this.hasIntakeTab(current) ? current : 'Questions'
  }

  @computed
  get capturedInformationTabs(): CapturedInformationTabItem[] {
    const tabs: CapturedInformationTabItem[] = [
      { id: 'Questions', label: 'Questions', testId: 'questions-tab-item' },
    ]
    if (this.notesVm.areNotesEnabled) {
      tabs.push({ id: 'Notes', label: 'Notes', testId: 'notes-tab-item' })
    }
    if (this._showsPersonalInfo) {
      tabs.push({
        id: 'PersonalInfo',
        label: 'Personal',
        testId: 'personal-info-tab-item',
        highlighted: true,
      })
    }
    if (this._showsBusinessInfo) {
      tabs.push({
        id: 'BusinessInfo',
        label: 'Business',
        testId: 'business-tab-item',
        highlighted: true,
      })
    }
    if (this._showsRefundInfo) {
      tabs.push({
        id: 'RefundInfo',
        label: 'Refund',
        testId: 'refund-tab-item',
        highlighted: true,
      })
    }
    return tabs
  }

  @computed
  private get _showsPersonalInfo() {
    const item = this.personalProfileVm?.item
    return Boolean(item && (item.completed || this.canEditCapturedInfo))
  }

  @computed
  private get _showsBusinessInfo() {
    const item = this.businessInfoVm?.item
    return Boolean(item && (item.completed || this.canEditCapturedInfo))
  }

  @computed
  private get _showsRefundInfo() {
    const refund = this.taxRefundMethodVm
    if (
      !refund ||
      (!refund.personalRefundMethodSupportingItem &&
        !refund.businessRefundMethodSupportingItem)
    ) {
      return false
    }
    return Boolean(refund.isRelevant) || this.canEditCapturedInfo
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
    // The intake renders a failure from the task's state.
    void this.fetch(this._lifecycle.signal).catch(() => undefined)
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  hasIntakeTab(tab: ResponsesInformationTab) {
    return this.intakeTabs.some((t) => t.value === tab)
  }

  /**
   * Loads the checklist.
   */
  fetch = task(async (signal: AbortSignal) => {
    await this._infoGatheringStore.fetchChecklist(this._job.id, signal)
    this._revalidation.markLoaded()
  })
}

function toList<T>(value: T | null | undefined): T[] {
  return value ? [value] : []
}
